// Redux/Slices/Pages/Provider/ServicesSlice.js
// Manages: services offered by a provider's business/professional workspace
// BZV2 backend: /api/v2/users/workspaces/:id/services

import { createSlice } from '@reduxjs/toolkit';
import { axiosInstance } from '../../../../Common/Interceptors/AxiosConfig';
import { showNotification } from '../../../../Common/Toast/Toast';

const CACHE_DURATION = 2 * 60 * 1000; // 2 minutes

const initialState = {
  // Services linked to a workspace
  servicesByWorkspaceId: {},     // { workspaceId: [services] }
  isLoading: false,
  fetchedAt: {},                // { workspaceId: timestamp }

  // Action states
  isProcessing: false,
  error: null,
};

const providerServicesSlice = createSlice({
  name: 'providerServices',
  initialState,
  reducers: {
    setLoading: (state, action) => { state.isLoading = action.payload; },
    setProcessing: (state, action) => { state.isProcessing = action.payload; },
    setServices: (state, action) => {
      const { workspaceId, services } = action.payload;
      state.servicesByWorkspaceId[String(workspaceId)] = services;
      state.fetchedAt[String(workspaceId)] = Date.now();
      state.isLoading = false;
    },
    updateServiceState: (state, action) => {
      const { workspaceId, serviceId, data } = action.payload;
      const services = state.servicesByWorkspaceId[workspaceId] || [];
      const idx = services.findIndex(s => s._id === serviceId);
      if (idx !== -1) {
        services[idx] = { ...services[idx], ...data };
      }
    },
    removeServiceState: (state, action) => {
      const { workspaceId, serviceId } = action.payload;
      if (state.servicesByWorkspaceId[workspaceId]) {
        state.servicesByWorkspaceId[workspaceId] = state.servicesByWorkspaceId[workspaceId].filter(
          s => s._id !== serviceId
        );
      }
    },
    setError: (state, action) => {
      state.error = action.payload;
      state.isLoading = false;
      state.isProcessing = false;
    },
    clearError: (state) => { state.error = null; },
  },
});

export const {
  setLoading, setProcessing, setServices,
  updateServiceState, removeServiceState, setError, clearError
} = providerServicesSlice.actions;

const getErrorMessage = (error, fallback = 'Something went wrong') =>
  error?.response?.data?.message || error?.message || fallback;

/** GET /users/workspaces/:id/services (Private Management) */
export const fetchWorkspaceServices = (workspaceId, force = false) => async (dispatch, getState) => {
  if (!workspaceId) return;

  const state = getState()?.providerServices;
  const fetchedAt = state?.fetchedAt?.[workspaceId];
  if (!force && fetchedAt && Date.now() - fetchedAt < CACHE_DURATION) return;

  dispatch(setLoading(true));
  try {
    const response = await axiosInstance.get(`/users/workspaces/${workspaceId}/services`);
    const { success, data } = response.data || {};

    if (success) {
      dispatch(setServices({ workspaceId, services: data || [] }));
    }
  } catch (error) {
    const errorMsg = getErrorMessage(error, 'Failed to fetch services');
    dispatch(setError(errorMsg));
    showNotification({ message: errorMsg, type: 'error' });
  }
};

/** GET /discovery/details/:id/services (Public Discovery) */
export const fetchPublicWorkspaceServices = (workspaceId) => async (dispatch, getState) => {
  if (!workspaceId) return;

  const state = getState()?.providerServices;
  const fetchedAt = state?.fetchedAt?.[workspaceId];
  if (fetchedAt && Date.now() - fetchedAt < CACHE_DURATION) return;

  dispatch(setLoading(true));
  try {
    const response = await axiosInstance.get(`/discovery/details/${workspaceId}/services`);
    const { success, data } = response.data || {};

    if (success) {
      dispatch(setServices({ workspaceId, services: data || [] }));
    }
  } catch (error) {

  } finally {
    dispatch(setLoading(false));
  }
};

/** POST /users/workspaces/:id/services */
export const addServiceToWorkspace = (workspaceId, payload) => async (dispatch) => {
  dispatch(setProcessing(true));
  try {
    const response = await axiosInstance.post(`/users/workspaces/${workspaceId}/services`, payload);
    const { success, data } = response.data || {};

    if (success) {
      // Refresh list to get populated service details (force bypass cache)
      dispatch(fetchWorkspaceServices(workspaceId, true));
      showNotification({ message: 'Service added successfully!', type: 'success' });
      return { success: true, data };
    }
  } catch (error) {
    const errorMsg = getErrorMessage(error, 'Failed to add service');
    showNotification({ message: errorMsg, type: 'error' });
    return { success: false, error: errorMsg };
  } finally {
    dispatch(setProcessing(false));
  }
};

/** POST /users/workspaces/:id/custom-services */
export const createCustomServiceAction = (workspaceId, payload) => async (dispatch) => {
  dispatch(setProcessing(true));
  try {
    const response = await axiosInstance.post(`/users/workspaces/${workspaceId}/custom-services`, payload);
    const { success, data } = response.data || {};

    if (success) {
      dispatch(fetchWorkspaceServices(workspaceId, true));
      showNotification({ message: data?.message || 'Service created successfully!', type: 'success' });
      return { success: true, data };
    }
  } catch (error) {
    const errorMsg = getErrorMessage(error, 'Failed to add custom product');
    showNotification({ message: errorMsg, type: 'error' });
    return { success: false, error: errorMsg };
  } finally {
    dispatch(setProcessing(false));
  }
};

/** PATCH /users/workspaces/:id/services/:wsServiceId */
export const updateWorkspaceServiceAction = (workspaceId, serviceId, data) => async (dispatch) => {
  dispatch(setProcessing(true));
  try {
    const response = await axiosInstance.patch(`/users/workspaces/${workspaceId}/services/${serviceId}`, data);
    const { success } = response.data || {};

    if (success) {
      dispatch(fetchWorkspaceServices(workspaceId, true));
      showNotification({ message: 'Service updated!', type: 'success' });
    }
  } catch (error) {
    const errorMsg = getErrorMessage(error, 'Failed to update service');
    showNotification({ message: errorMsg, type: 'error' });
  } finally {
    dispatch(setProcessing(false));
  }
};

/** DELETE /users/workspaces/:id/services/:wsServiceId */
export const deleteWorkspaceServiceAction = (workspaceId, serviceId) => async (dispatch) => {
  dispatch(setProcessing(true));
  try {
    const response = await axiosInstance.delete(`/users/workspaces/${workspaceId}/services/${serviceId}`);
    const { success } = response.data || {};

    if (success) {
      dispatch(removeServiceState({ workspaceId, serviceId }));
      showNotification({ message: 'Service removed', type: 'success' });
    }
  } catch (error) {
    const errorMsg = getErrorMessage(error, 'Failed to remove service');
    showNotification({ message: errorMsg, type: 'error' });
  } finally {
    dispatch(setProcessing(false));
  }
};

export default providerServicesSlice.reducer;
